import { ActionFunctionMap, ActionObject, assign } from 'xstate';

import {
  MicroEstimationContext,
  MicroEstimationEventType,
  MicroEstimationtEvent,
} from '.';

export enum MicroEstimationAction {
  UpdateAccreStepContext = 'updateAccreStepContext',
  UpdateTaxFrequencyStepContext = 'updateTaxFrequencyStepContext',
  UpdateTaxPeriodRevenueContext = 'updateTaxPeriodRevenueContext',
  UpdateRevenueSituationStepContext = 'updateRevenueSituationStepContext',
  UpdateVatSituationStepContext = 'updateVatSituationStepContext',
  UpdateCompanySituationStepContext = 'updateCompanySituationStepContext',
  UpdatePrelevLibStepContext = 'updatePrelevLibStepContext',
  UpdateTaxTypeStepContext = 'updateTaxTypeStepContext',
  UpdateVatStepContext = 'updateVatStepContext',
}

// Wrapper to avoid having to add generics to each assign call separately
const assignContext = (
  callback: (
    context: MicroEstimationContext,
    event: MicroEstimationtEvent,
  ) => MicroEstimationContext,
) => assign(callback);

export const microEstimationActions = {
  updateAccreStepContext: assignContext((context, event) => {
    if (event.type === MicroEstimationEventType.SAVE_ACCRE_STEP) {
      return {
        ...context,
        companyProfile: {
          ...context.companyProfile,
          hasAccre: event.data.hasAccre,
        },
      };
    }

    return context;
  }),
  updateCompanySituationStepContext: assignContext((context, event) => {
    if (!context.taxForecastProfile) {
      throw new Error(
        'TaxForecastProfile must be defined when calling updateCompanySituationStepContext',
      );
    }

    if (event.type === MicroEstimationEventType.SAVE_COMPANY_SITUATION_STEP) {
      return {
        ...context,
        taxForecastProfile: {
          ...context.taxForecastProfile,
          isReglementedLiberal: event.data.isReglementedLiberal,
          mainActivityType: event.data.mainActivityType,
          subActivity: event.data.subActivity,
        },
      };
    }

    return context;
  }),
  updatePrelevLibStepContext: assignContext((context, event) => {
    if (event.type === MicroEstimationEventType.SAVE_PRELEV_LIB_STEP) {
      return {
        ...context,
        companyProfile: {
          ...context.companyProfile,
          hasPrelevLib: event.data.hasPrelevLib,
        },
      };
    }

    return context;
  }),
  updateRevenueSituationStepContext: assignContext((context, event) => {
    if (!context.taxForecastPeriod) {
      throw new Error(
        'TaxForecastPeriod must be defined when calling updateRevenueSituationStepContext',
      );
    }

    if (event.type === MicroEstimationEventType.SAVE_REVENUE_SITUATION_STEP) {
      return {
        ...context,
        taxForecastPeriod: {
          ...context.taxForecastPeriod,
          bicDistributionAmount: event.data.bicDistributionAmount,
          bncDistributionAmount: event.data.bncDistributionAmount,
          complementaryRevenue: event.data.complementaryRevenue,
          sellingDistributionAmount: event.data.sellingDistributionAmount,
        },
      };
    }

    return context;
  }),
  updateTaxFrequencyStepContext: assignContext((context, event) => {
    if (event.type === MicroEstimationEventType.SAVE_TAX_FREQUENCY_STEP) {
      return {
        ...context,
        companyProfile: {
          ...context.companyProfile,
          taxFrequency: event.data.taxFrequency,
        },
        taxForecastPeriod: event.data.taxForecastPeriod,
        taxForecastProfile: event.data.taxForecastProfile,
      };
    }

    return context;
  }),
  updateTaxPeriodRevenueContext: assignContext((context, event) => {
    if (!context.taxForecastPeriod) {
      throw new Error(
        'TaxForecastPeriod must be defined when calling updateTaxPeriodRevenueContext',
      );
    }

    if (event.type === MicroEstimationEventType.UPDATE_REVENUE) {
      return {
        ...context,
        taxForecastPeriod: {
          ...context.taxForecastPeriod,
          revenue: event.revenue,
        },
      };
    }

    return context;
  }),
  updateTaxTypeStepContext: assignContext((context, event) => {
    if (!context.taxForecastProfile) {
      throw new Error(
        'TaxForecastProfile must be defined when calling updateTaxTypeStepContext',
      );
    }

    if (event.type === MicroEstimationEventType.SAVE_TAX_TYPE_STEP) {
      return {
        ...context,
        taxForecastProfile: {
          ...context.taxForecastProfile,
          ...event.data,
        },
      };
    }

    return context;
  }),
  updateVatSituationStepContext: assignContext((context, event) => {
    if (!context.taxForecastPeriod) {
      throw new Error(
        'TaxForecastPeriod must be defined when calling updateVatSituationStepContext',
      );
    }

    if (event.type === MicroEstimationEventType.SAVE_VAT_SITUATION_STEP) {
      return {
        ...context,
        taxForecastPeriod: {
          ...context.taxForecastPeriod,
          vatOffset: event.data.vatOffset,
          vatRate: event.data.vatRate,
        },
      };
    }

    return context;
  }),
  updateVatStepContext: assignContext((context, event) => {
    if (!context.taxForecastProfile) {
      throw new Error(
        'TaxForecastProfile must be defined when calling updateVatStepContext',
      );
    }

    if (event.type === MicroEstimationEventType.SAVE_VAT_STEP) {
      return {
        ...context,
        taxForecastProfile: {
          ...context.taxForecastProfile,
          isSubjectToVat: event.data.isSubjectToVat,
        },
      };
    }

    return context;
  }),
} satisfies Record<
  MicroEstimationAction,
  ActionFunctionMap<
    MicroEstimationContext,
    MicroEstimationtEvent,
    ActionObject<MicroEstimationContext, MicroEstimationtEvent>
  >
>;
